import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Calendar,
  Users,
  Building2,
  ClipboardCheck,
  Settings,
  LogOut,
  X,
  Send,
  User,
  HelpCircle,
} from 'lucide-react';

import { useAppSelector, useAppDispatch } from '@/store';
import { selectCurrentUser, logout } from '@/store/slices/authSlice';
import { logout as apiLogout } from '@/services/api/authService';
import { UserRole } from '@/types';

interface MobileMoreMenuProps {
  open: boolean;
  onClose: () => void;
}

/**
 * MobileMoreMenu
 * Slide-up drawer showing additional navigation options on mobile
 */
const MobileMoreMenu: React.FC<MobileMoreMenuProps> = ({ open, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);

  const isManager = user?.role && [
    UserRole.TEAM_LEAD,
    UserRole.PROJECT_MANAGER,
    UserRole.DEPARTMENT_HEAD,
    UserRole.ADMIN,
    UserRole.MANAGER,
  ].includes(user.role);
  const isAdmin = user?.role === UserRole.ADMIN;
  const canViewSettings = isManager || isAdmin;

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    onClose();
    await apiLogout();
    dispatch(logout());
    navigate('/login');
  };

  const isActivePath = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const menuItems = [
    { path: '/calendar', label: 'Calendar', icon: Calendar, show: true },
    { path: '/delegated', label: 'Delegated Tasks', icon: Send, show: true },
    { path: '/team', label: 'Team', icon: Users, show: isManager },
    { path: '/departments', label: 'Departments', icon: Building2, show: isManager },
    { path: '/reviews', label: 'Reviews & Approval', icon: ClipboardCheck, show: isManager },
    { path: '/users', label: 'Users', icon: User, show: isAdmin },
  ];

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh',
          pb: 'env(safe-area-inset-bottom)',
        },
      }}
    >
      {/* Handle bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 4,
            bgcolor: 'grey.300',
            borderRadius: 2,
          }}
        />
      </Box>

      {/* Header with close button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          More Options
        </Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </Box>

      {/* User Profile Card */}
      <Box
        sx={{
          mx: 2,
          mb: 2,
          p: 2,
          bgcolor: 'grey.50',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
        onClick={canViewSettings ? () => handleNavigation('/settings') : undefined}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: 'primary.main',
            fontSize: '1.25rem',
            fontWeight: 600,
          }}
        >
          {user?.firstName?.[0]?.toUpperCase() || 'U'}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {user?.displayName || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Chip
          label={user?.role?.replace('_', ' ') || 'User'}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ textTransform: 'capitalize', fontSize: '0.65rem' }}
        />
      </Box>

      <Divider />

      {/* Navigation Items */}
      <List sx={{ px: 1, py: 1 }}>
        {menuItems
          .filter((item) => item.show)
          .map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 2,
                    minHeight: 52,
                    bgcolor: isActive ? 'primary.50' : 'transparent',
                    color: isActive ? 'primary.main' : 'text.primary',
                    '&:active': {
                      bgcolor: isActive ? 'primary.100' : 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 44,
                      color: 'inherit',
                    }}
                  >
                    <Icon size={22} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>
      <Divider />

      {/* Settings & Help */}
      <List sx={{ px: 1, py: 1 }}>
        {canViewSettings && (
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation('/settings')}
              sx={{
                borderRadius: 2,
                minHeight: 52,
                bgcolor: isActivePath('/settings') ? 'primary.50' : 'transparent',
                color: isActivePath('/settings') ? 'primary.main' : 'text.primary',
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <Settings size={22} />
              </ListItemIcon>
              <ListItemText
                primary="Settings"
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        )}

        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            sx={{ borderRadius: 2, minHeight: 52 }}
            onClick={() => {/* Open help */}}
          >
            <ListItemIcon sx={{ minWidth: 44, color: 'text.secondary' }}>
              <HelpCircle size={22} />
            </ListItemIcon>
            <ListItemText
              primary="Help & Support"
              primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      <Divider />

      {/* Logout */}
      <List sx={{ px: 1, py: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              minHeight: 52,
              color: 'error.main',
              '&:active': {
                bgcolor: 'error.50',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 44, color: 'inherit' }}>
              <LogOut size={22} />
            </ListItemIcon>
            <ListItemText
              primary="Sign Out"
              primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      {/* Bottom spacing for safe area */}
      <Box sx={{ height: 8 }} />
    </Drawer>
  );
};

export default MobileMoreMenu;
